// src/components/CustomizeMembership/CustomizeMembership.js

import React, { useState, useRef, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import { auth } from "../../firebaseConfig";

import "../../styles/ijrr.css";
import "./CustomizeMembership.css";
import ImageEditor from "./ImageEditor";
import MembershipCheckout from "./MembershipCheckout";

import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function CustomizeMembership() {
    const [uploadedImages, setUploadedImages] = useState([]);
    const [selectedFlag, setSelectedFlag] = useState('/assets/images/defaults/flag_white.png');
    const [hoveredFlag, setHoveredFlag] = useState(null);
    const [previewOverlay, setPreviewOverlay] = useState(null);
    const editorRef = useRef(null);
    const navigate = useNavigate();

    const [showCheckoutSection, setShowCheckoutSection] = useState(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
    
    // States for loading
    const [isAssetUploading, setIsAssetUploading] = useState(false); 
    const [isCanvasProcessing, setIsCanvasProcessing] = useState(false); 

    // States to hold data for the checkout component
    const [rawAssetFile, setRawAssetFile] = useState(null);
    const [finalCanvasBlob, setFinalCanvasBlob] = useState(null);
    
    // Track current selections
    const [currentAssetUrl, setCurrentAssetUrl] = useState(null);
    const [currentAssetId, setCurrentAssetId] = useState(null);

    // Helper to generate proxy URL
    const getProxyUrl = (url) => {
        if (!url) return null;
        // If it's a data URI (base64 from upload), return as is
        if (url.startsWith('data:')) return url;
        // Otherwise, wrap in proxy to ensure CORS headers
        return `${API_BASE_URL}/customer-assets/download-proxy?url=${encodeURIComponent(url)}`;
    };

    // Helper to convert a URL back to a File object using our Proxy Route
    const convertUrlToFile = async (url) => {
        try {
            const proxyUrl = getProxyUrl(url);
            const response = await fetch(proxyUrl);
            
            if (!response.ok) throw new Error("Failed to fetch image via proxy");

            const blob = await response.blob();
            return new File([blob], "selected_gallery_image.png", { type: blob.type });
        } catch (error) {
            console.error("Error converting gallery URL to file:", error);
            try {
                const directResponse = await fetch(url, { mode: 'cors' }); 
                const directBlob = await directResponse.blob();
                return new File([directBlob], "selected_gallery_image.png", { type: directBlob.type });
            } catch (e) {
                return null;
            }
        }
    };

    // 1. Initial Load: Get user and assets
    useEffect(() => {
        const fetchAssets = async (uid) => {
            try {
                const response = await fetch(`${API_BASE_URL}/customer-assets/list`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ firebase_uid: uid })
                });

                if (response.ok) {
                    const data = await response.json();
                    setUploadedImages(data.assets);
                    
                    const mainAsset = data.assets.find(asset => asset.is_main);
                    if (mainAsset) {
                        const proxyUrl = getProxyUrl(mainAsset.url);
                        // Pass Proxy URL to editor so it loads with CORS headers
                        setPreviewOverlay(proxyUrl);
                        setCurrentAssetUrl(mainAsset.url); // Keep original URL for ID tracking
                        setCurrentAssetId(mainAsset.id);
                        
                        const file = await convertUrlToFile(mainAsset.url);
                        if (file) setRawAssetFile(file);
                    }
                }
            } catch (error) {
                console.error("Failed to fetch customer assets:", error);
            }
        };

        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                fetchAssets(user.uid);
            } else {
                setUploadedImages([]); 
            }
        });

        return () => unsubscribe();
    }, []);


    // Helper: Reset checkout if user changes design
    const resetCheckout = () => {
        if (showCheckoutSection) {
            setShowCheckoutSection(false);
            setFinalCanvasBlob(null);
        }
    };

    const handleFileUpload = async (file) => {
        if (!file || !file.type.startsWith("image/")) return;

        resetCheckout(); 

        const tempId = `temp-${Date.now()}`;

        const reader = new FileReader();
        reader.onload = (e) => {
            const tempUrl = e.target.result; // This is base64, safe for canvas
            
            setPreviewOverlay(tempUrl);
            setCurrentAssetUrl(tempUrl);
            setRawAssetFile(file); 

            const newTempAsset = { 
                id: tempId, 
                url: tempUrl, 
                is_main: true, 
                created_at: new Date().toISOString() 
            };

            setUploadedImages(prev => [
                newTempAsset, 
                ...prev.map(asset => ({ ...asset, is_main: false }))
            ]);
        };
        reader.readAsDataURL(file);

        // Upload to Backend
        const user = auth.currentUser;
        if (user) {
            setIsAssetUploading(true);
            const formData = new FormData();
            formData.append('firebase_uid', user.uid);
            formData.append('image', file);
            formData.append('set_as_main', '1'); 
            
            try {
                const response = await fetch(`${API_BASE_URL}/customer-assets/upload`, {
                    method: 'POST',
                    body: formData,
                });
                
                if (response.ok) {
                    const data = await response.json();
                    const newRealAsset = data.asset; 

                    setUploadedImages(prev => 
                        prev.map(asset => {
                            if (asset.id === tempId) {
                                return { ...newRealAsset, url: newRealAsset.url }; 
                            }
                            return asset;
                        })
                    );
                    
                    setCurrentAssetId(newRealAsset.id);
                    toast.success("Image uploaded to your gallery!", { autoClose: 2000 });
                } else {
                    toast.error("Failed to save image to server.");
                }
            } catch (error) {
                console.error("Asset upload error:", error);
                toast.error("Asset upload failed due to network error.");
            } finally {
                setTimeout(() => setIsAssetUploading(false), 500);
            }
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        handleFileUpload(file);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleBrowseClick = () => {
        document.getElementById("fileInput").click();
    };

    const onFileChange = (e) => {
        const file = e.target.files[0];
        handleFileUpload(file);
    };

    // UPDATED LOGIC: 
    const handleImageClick = async (asset) => {
        if (!asset.id || String(asset.id).startsWith('temp-')) return;

        resetCheckout(); 

        const proxyUrl = getProxyUrl(asset.url);

        // 1. Update visual state using PROXY URL (for Canvas safety)
        if (editorRef.current?.addOverlayImage) {
            editorRef.current.addOverlayImage(proxyUrl);
        }
        
        // 2. Prepare states 
        setPreviewOverlay(proxyUrl); // This ensures Row 2 previews use the safe URL too
        setCurrentAssetUrl(asset.url);
        setCurrentAssetId(asset.id);

        // 3. Prepare File for Checkout
        const file = await convertUrlToFile(asset.url);
        if (file) {
            setRawAssetFile(file);
        }
        
        // 4. Mark as main in backend
        const user = auth.currentUser;
        if (user && asset.id) {
            try {
                await fetch(`${API_BASE_URL}/customer-assets/set-main`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        firebase_uid: user.uid,
                        asset_id: asset.id,
                    }),
                });

                setUploadedImages(prev => 
                    prev.map(a => ({ 
                        ...a, 
                        is_main: a.id === asset.id 
                    }))
                );
                toast.info("Image selected.", { autoClose: 1000 });
            } catch (error) {
                console.error("Set main asset error:", error);
            }
        }
    };

    const handleFlagSelect = (color) => {
        resetCheckout(); 
        switch (color) {
            case 'white':
                setSelectedFlag('/assets/images/defaults/flag_white.png');
                break;
            case 'black':
                setSelectedFlag('/assets/images/defaults/flag_black.png');
                break;
            case 'blue':
                setSelectedFlag('/assets/images/defaults/flag_blue.png');
                break;
            default:
                setSelectedFlag('/assets/images/defaults/flag_white.png');
        }
    };

    const getFlagUrl = (color) => {
        switch (color) {
            case 'white':
                return '/assets/images/defaults/flag_white.png';
            case 'black':
                return '/assets/images/defaults/flag_black.png';
            case 'blue':
                return '/assets/images/defaults/flag_blue.png';
            default:
                return '/assets/images/defaults/flag_white.png';
        }
    };

    const handleSaveAndCheckout = async () => {
        if (!previewOverlay) {
            setIsErrorModalOpen(true);
            return;
        }

        const user = auth.currentUser;
        if (!user) {
            toast.error("Please log in to save your design and checkout.");
            return;
        }

        if (!rawAssetFile) {
            toast.warning("Processing image... please try clicking 'Save & Checkout' again in a moment.");
            if (currentAssetUrl) {
                const file = await convertUrlToFile(currentAssetUrl);
                if (file) {
                    setRawAssetFile(file);
                    return; 
                }
            }
            return;
        }

        setIsCanvasProcessing(true);
        try {
            const blob = await editorRef.current.exportCanvasAsPNG();
            setFinalCanvasBlob(blob);

            setShowCheckoutSection(true);
            
            setTimeout(() => {
                const element = document.getElementById('checkout-anchor');
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 500);

        } catch (error) {
            console.error("Canvas export error:", error);
            toast.error("Could not process design. Please try again.");
        } finally {
            setIsCanvasProcessing(false);
        }
    };

    const handleClearOverlay = () => {
        setPreviewOverlay(null);
        resetCheckout();
    };

    const getSelectedColorString = () => {
        if(selectedFlag.includes('white')) return 'white';
        if(selectedFlag.includes('black')) return 'black';
        if(selectedFlag.includes('blue')) return 'blue';
        return 'white';
    }
    
    // Display URL for the gallery thumbnails can remain direct, 
    // as <img> tags don't have the same strict CORS requirements as Canvas
    const getDisplayImageUrl = (imgObj) => imgObj.url;

    return (
        <section className="customize-membership-section space-pb-80">
            <ToastContainer />
            <div className="container text-center">
                <h2 className="original-title golden mb-3">Customize Your Membership</h2>
                <p className="ijrr-text mb-5">
                    Upload your image, choose your flag, and proceed to checkout to complete your official <strong>IJRR membership</strong>.
                </p>
            </div>

            {/* --- ROW 1: Upload & Editor --- */}
            <div className="custom-container">
                <div className="row">
                    <div className="col-md-4 cdx-xl-55">
                        <div
                            className="upload-area"
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                        >
                            <div className={`ijrr-upload-loader ${isAssetUploading ? 'active' : ''}`}>
                                <i className="fa fa-spinner ijrr-loader-icon"></i>
                                <span className="ijrr-loader-text">Uploading to gallery...</span>
                            </div>

                            <input
                                type="file"
                                id="fileInput"
                                accept="image/*"
                                onChange={onFileChange}
                                style={{ display: "none" }}
                            />
                            <i className="fa fa-cloud-upload upload-icon"></i>
                            <p>Drag & Drop your image here</p>
                            <button
                                type="button"
                                className="btn btn-primary mt-2"
                                onClick={handleBrowseClick}
                            >
                                Browse Your Computer
                            </button>
                            <small>Supported: JPG, PNG, SVG (max 5MB)</small>
                            
                            {/* --- GALLERY SECTION --- */}
                            {uploadedImages.length > 0 && (
                                <div className="uploaded-preview mt-4">
                                    {uploadedImages.map((asset, index) => (
                                        <img
                                            key={asset.id || `temp-${index}`} 
                                            src={getDisplayImageUrl(asset)}
                                            alt={`Asset ${index + 1}`}
                                            className={`img-fluid uploaded-thumbnail ${asset.is_main ? 'active-asset' : ''}`}
                                            onClick={() => handleImageClick(asset)}
                                            title={asset.is_main ? "Current Main Image" : "Click to use this image"}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="col-md-8 cdx-xl-45">
                        <div className="editor-wrap">
                            <ImageEditor
                                ref={editorRef}
                                selectedImageUrl={hoveredFlag || selectedFlag}
                                overlayImageUrl={previewOverlay}
                                onOverlayCleared={handleClearOverlay}
                            />
                        </div>

                        <div className="color-and-button-row">
                            <div className="color-options">
                                {['white', 'black', 'blue'].map((color) => (
                                    <div
                                        key={color}
                                        className={`color-circle color-${color} ${selectedFlag.includes(`flag_${color}`) ? 'active' : ''}`}
                                        title={color.charAt(0).toUpperCase() + color.slice(1)}
                                        onClick={() => handleFlagSelect(color)}
                                        onMouseEnter={() => setHoveredFlag(getFlagUrl(color))}
                                        onMouseLeave={() => setHoveredFlag(null)}
                                    ></div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* --- ROW 2: Previews & Save Button --- */}
            <div className="custom-container mt-5">
                <div className="membership-products-box">
                    <div className="product-item image-overlay">
                        <img src={selectedFlag} alt="Flag Preview" className="flag-bg" />
                        {previewOverlay && (
                            <div className="overlay-img-container">
                                <img src={previewOverlay} alt="Overlay" className="overlay-img" />
                            </div>
                        )}
                        <p>Custom Flag (2'x3')</p>
                    </div>

                    <div className="product-item sticker-overlay">
                        <img src="/assets/images/defaults/sticker_bg.png" alt="Sticker Preview" className="sticker-bg" />
                        {previewOverlay && (
                            <div className="sticker-overlay-img-container">
                                <img src={previewOverlay} alt="Overlay" className="sticker-overlay-img" />
                            </div>
                        )}
                        <p>Custom Sticker (3"x3")</p>
                    </div>

                    <div className="product-item certificate-overlay">
                        <img src="/assets/images/defaults/parchment_bg.png" alt="Certificate" className="certificate-bg" />
                        {previewOverlay && (
                            <div className="certificate-overlay-container">
                                <img src={previewOverlay} alt="Overlay" className="certificate-overlay-img" />
                            </div>
                        )}
                        <p>Membership Certificate</p>
                    </div>

                    {/* COL 4: Features + Button */}
                    <div className="product-column-wrapper">
                        
                        <div className="text-item benefits-box">
                            <ul className="membership-benefits-list">
                                <li><i className="fa fa-check text-success"></i> <strong>Member-only content</strong></li>
                                <li><i className="fa fa-check text-success"></i> <strong>Exclusive forums</strong></li>
                                <li><i className="fa fa-check text-success"></i> <strong>Pirate news & updates</strong></li>
                                <li><i className="fa fa-check text-success"></i> <strong>Event invitations</strong></li>
                                <li><i className="fa fa-check text-success"></i> <strong>Global recognition</strong></li>
                            </ul>
                        </div>

                        {previewOverlay && !showCheckoutSection && (
                            <div className="animate__animated animate__fadeIn">
                                <button 
                                    className="btn-save-checkout" 
                                    onClick={handleSaveAndCheckout}
                                    disabled={isCanvasProcessing}
                                >
                                    {isCanvasProcessing ? (
                                        <>
                                            <i className="fa fa-spinner fa-spin me-2"></i> Processing...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa fa-shopping-cart me-2"></i> Save & Checkout
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                        
                    </div>
                </div> 
            </div>

            {/* --- ROW 3: Checkout Section --- */}
            <div id="checkout-anchor"></div> 
            
            {showCheckoutSection && (
                <div className="animate__animated animate__fadeInUp mt-4">
                        <MembershipCheckout 
                        rawAssetFile={rawAssetFile}
                        finalCanvasBlob={finalCanvasBlob}
                        selectedFlagColor={getSelectedColorString()}
                        />
                </div>
            )}

            {isErrorModalOpen && (
                <div className="error-modal" onClick={() => setIsErrorModalOpen(false)}>
                    <div className="error-content" onClick={(e) => e.stopPropagation()}>
                        <button className="close-btn" onClick={() => setIsErrorModalOpen(false)}>×</button>
                        <h3>Before you can proceed with the purchase</h3>
                        <p>You must select or upload at least one image.</p>
                        <button className="btn btn-primary" onClick={() => setIsErrorModalOpen(false)}>Close</button>
                    </div>
                </div>
            )}
        </section>
    );
}